<template>
    <div class="position-relative" :class="{'has-overlay': loading}">
        <div class="d-flex flex-row">
            <div class="h5 flex-grow-1" >
                {{ getHeader(object.id) }}
            </div>

            <div class="d-flex">
                <div class="flex-grow-0">
                    <b-button variant="outline-secondary" size="lg" class="border-0 p-0"  @click="onCancel">
                        <b-icon icon="x" />
                    </b-button>
                </div>
            </div>
        </div>

        <raid-status-cell v-if="object.id" :value="object.statusObject" />
        <small class="text-secondary d-flex flex-column mt-2">
            <span>{{ request.objectLabel() }}</span>
            <span>{{ request.description }}</span>
        </small>

        <div>
            <b-form-row class="mt-4 mb-2">
                <form-field class="col-8" v-if="object.id"
                             :editable="false"
                            :label="getFieldLabel('caughtCount')"
                             :value="object.caughtCount"
                             :feedback="liveFeedback('caughtCount')">
                    <template #value>
                        <span class="mr-2">{{ object.caughtCount }}</span>
                        <b-button v-if="canAddEvents && canEditRaid"
                                  :disabled="loading"
                                  variant="success" size="sm"
                                  @click="onCreateEvents">Добавить</b-button>
                    </template>
                </form-field>
            </b-form-row>

            <b-form-row class="mt-2 mb-2" >
                <date-time-picker class="col-12"
                                  title="Запланирован"
                                  name="plannedAt"
                                  :key="object.plannedAt"
                                  :editable="getAbility('plannedAt') && canModifyRaidParameters"
                                  :show-timepicker="true"
                                  :initial-date-string="getInitialDateString()"
                                  :state="validateState('plannedAt')"
                                  @date-changed="onModelDateChanged" />
            </b-form-row>

            <b-form-row class="mt-4 mb-2" v-if="object.id && isCompleted">
                <date-time-picker class="col-12"
                                  title="Проведен"
                                  name="actualDate"
                                  :editable="false"
                                  :show-timepicker="true"
                                  :date-required="false"
                                  :initial-date-string="object.actualDate"
                                  :state="validateState('actualDate')"
                                  @date-changed="onModelDateChanged" />
            </b-form-row>

            <b-form-row class="mt-2 mb-2">
                    <form-field
                            :label="getFieldLabel('address')"
                            class="col-12"
                            :class="{ 'required': getAbility('address') && canModifyRaidParameters }"
                            :editable="getAbility('address') && canModifyRaidParameters"
                            :feedback="liveFeedback('address')"
                            :value="form.addressHumanReadable ? form.addressHumanReadable : 'Не указан'">

                        <template #value>
                            <map-box ref="map" :coordinates="selectedCoordinates" style="width: 100%; height: 500px"/>
                            <div class="clearfix p-1"></div>
                            <div class="col-12 p-0 m-0 d-flex align-items-center">
                                <div class="flex-grow-1 mr-2">{{ form.addressHumanReadable ? form.addressHumanReadable : 'Не указан' }}</div>
                                <b-button variant="link" class="p-0" @click="onAddressFocus">
                                    <img :src="focus" width="32"/>
                                </b-button>
                            </div>
                        </template>

                        <template #field>
                            <map-box ref="map" :coordinates="selectedCoordinates" v-on:map-clicked="onMapClicked" style="width: 100%; height: 500px"/>
                            <div class="clearfix p-1"></div>
                            <div class="col-12 p-0 m-0 d-flex">
                                <single-select name="address"
                                               ref="address"
                                               class="flex-grow-1 mr-2"
                                               v-model="form['addressObject']"
                                               :url="getAddressUrl"
                                               :label="getFieldLabel('address')"
                                               label-key="human_readable_address"
                                               :state="getValidationClass('address')"
                                               :has-pagination="false"
                                               v-on:select-search-init="onAddressSearchStart"
                                               v-on:select-changed="onAddressChanged"
                                               v-on:select-set="onAddressSet"
                                               v-on:select-cleared="onModelObjectSingleSelectCleared" />
                                <b-button variant="link" class="p-0" @click="onAddressFocus">
                                    <img :src="focus" width="32"/>
                                </b-button>
                            </div>
                        </template>
                    </form-field>
            </b-form-row>

            <b-form-row v-if="object.id">
                <form-field :label="getFieldLabel('comment')"
                            :value="object.comment"
                            :editable="getAbility('comment')"
                            :feedback="liveFeedback('comment')"
                            class="col-12 mt-2 mb-2">
                        <template #field>
                                <b-form-textarea
                                    id="comment"
                                    class="w-100"
                                     v-model="$v.form.comment.$model"
                                    :state="validateState('comment')">
                                </b-form-textarea>
                        </template>
                </form-field>
            </b-form-row>
        </div>

        <div class="d-flex col-12 mt-3 p-0">
            <div class="d-flex flex-grow-1 justify-content-end">
                <b-button @click="onCreate" :disabled="loading" v-if="!object.id && canCreateRaid" variant="primary">Создать</b-button>
                <b-button @click="onUpdate" :disabled="loading" v-if="object.id && $v.form.$anyDirty && canEditRaid" variant="primary">Сохранить</b-button>
                <b-button v-if="canModifyRaidStatus && canEditRaid && !isSuccess"
                          :disabled="loading"
                          variant="danger"
                          @click="onComplete" class="ml-2">Никого не поймали</b-button>
            </div>
        </div>

        <div class="overlay">
            <div class="d-flex w-100 h-100 align-items-center justify-content-center">
                <b-spinner variant="light" />
            </div>
        </div>



    </div>
</template>

<script>
import {ObjectComponentMixin} from "@/components/Forms/ObjectComponentMixin";
import {mapGetters} from "vuex";
import api from "@/api";
import {Raid, RaidStatus} from "@/pages/AppPages/Raids/raid";
import {todayStr} from "@/utils/dateFormat";
import RaidStatusCell from "@/pages/DataTableCells/RaidStatusCell.vue";
import FormField from "@/components/Forms/FormField.vue";
import DateTimePicker from "@/components/Forms/DateTimePicker.vue";
import SingleSelect from "@/components/Select/SingleSelect.vue";
import {ModalMixin} from "@/components/Forms/ModalMixin";
import {MapMixin} from "@/components/Map/mapMixin";
import MapBox from "@/components/Map/MapBox.vue";

export default {
    name: "MonitoringRaidModalDetail",
    components: {MapBox, SingleSelect, DateTimePicker, FormField, RaidStatusCell},
    mixins: [ObjectComponentMixin, ModalMixin, MapMixin],
    props: {
        raidId: { type: null, default: null },
        request: { type: Object, default: () => { return {} }},
    },
    computed: {
        ...mapGetters(['ability']),
        isCompleted() { return this.object.isCompleted; },
        getStatusUrl() { return api.raids.statuses.list(); },
        createUrl() { return api.raids.default.list(); },
        updateUrl() { return api.raids.default.detail(this.objectId); },
        canCreateRaid() {
            return this.ability.can('create', 'Raid')
        },
        canEditRaid() {
            return this.ability.can('update', this.object)
        },
        canModifyRaidStatus() {
            return this.object.id && !this.isFail
        },
        canModifyRaidParameters() {
            return !this.object.id && !this.isCompleted
        },
        canAddEvents() {
            return this.canModifyRaidStatus
        },
        isFail() {
            return this.object.status === RaidStatus.FAIL
        },
        isSuccess() {
            return this.object.status === RaidStatus.SUCCESS
        }
    },
    methods: {
        getInitialDateString() {
            return this.object.plannedAt;
        },
        onCreateEvents() {
            this.$router.replace({ name: 'raidMonitoringCreate', params: { raidId: this.object.id } })
        },
        onComplete() {
            if (this.object.id) {
                if (this.form.caughtCount > 0) {
                    this.form.status = RaidStatus.SUCCESS;
                } else {
                    this.form.status = RaidStatus.FAIL;
                }
                this.form.actualDate = todayStr();

                const action = this.getSubmitAction(this.object.id);
                action().then(response => {
                    this.$emit('object-modal-updated', response.data);
                    this.$bvToast.toast('Рейд выполнен', {
                        title: this.object.objectLabel(),
                        autoHideDelay: 5000,
                        variant: 'success'
                    });
                });
            }
        }
    },
    beforeMount() {
        this.objectClass = Raid;
        this.objectId = this.raidId;
        this.initObjectParams = {
            request: this.request.id,
            address: this.request.address,
            addressObject: this.request.addressObject,
            lat: this.request.lat,
            lon: this.request.lon,
            requestContractorId: this.request.assignedToId,
            plannedCount: null
        };
    },
    mounted() {
        const vm = this;
        vm.onMount();
        vm.resetForm().then(() => {
            if (vm.object.addressObject['id']) {
                vm.setMapCoordinates(vm.object.coordinates)
            }
        });
    }
}
</script>

<style scoped>

</style>